import clsx from 'clsx';
import React, { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import QRCode from 'react-qr-code';
import RankingCardBg from '../../assets/background/RankingCardBg';
import { toJpeg } from 'html-to-image';
import { API } from 'aws-amplify';
import Spinner from '../../components/Spinner';
import useUserCreatedReferral from '../../hooks/referral/useUserCreatedReferral';
import { useWeb3React } from '@web3-react/core';
import { getReferralLink } from '../../helpers';
import ShareCardLogo from '../../assets/logos/ShareCardLogo';

type Props = React.HtmlHTMLAttributes<HTMLDivElement> & {
    children: React.ReactNode;
    imageId: string;
    setImageId: Dispatch<SetStateAction<string>>;
    setImageBlob: Dispatch<SetStateAction<string>>;
};

const ShareCardBase: React.FC<Props> = ({
    className,
    children,
    imageId,
    setImageId,
    setImageBlob,
    ...rest
}) => {
    const { account } = useWeb3React();
    const { userCreatedReferral } = useUserCreatedReferral(account);

    const userRankingRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const card = userRankingRef.current;

        let imageDataPromise: Promise<any>;

        if (card && userCreatedReferral) {
            (async function () {
                try {
                    // setting timeout otherwise it delays rendering of component
                    setTimeout(async () => {
                        const dataUrl = await toJpeg(card as HTMLElement, {
                            quality: 0.95,
                            type: 'image/jpeg',
                        });

                        imageDataPromise = API.post('UploadImage', '/upload', {
                            body: dataUrl,
                            headers: {
                                'Content-Type': 'text/plain',
                            },
                        });

                        const imageData = await imageDataPromise;

                        setImageId(imageData);
                        setImageBlob(dataUrl);
                    }, 10);
                } catch (error) {
                    console.log(error);
                }
            })();
        }

        return () => {
            imageDataPromise && API.cancel(imageDataPromise);
        };
    }, [userCreatedReferral, setImageId, setImageBlob]);

    return (
        <div className='user-ranking-wrapper'>
            <div
                className={clsx('user-ranking', className)}
                {...rest}
                ref={userRankingRef}
            >
                <RankingCardBg />
                <div className='user-ranking-header'>
                    <ShareCardLogo />
                    <div className={clsx('referral', { hidden: !userCreatedReferral })}>
                        <div className='referral-infos'>
                            <span>Referral code</span>
                            <span className='referral-code'>{userCreatedReferral}</span>
                        </div>
                        <div className='referral-qrcode'>
                            <QRCode
                                value={getReferralLink(
                                    imageId,
                                    userCreatedReferral || ''
                                )}
                                size={256}
                                viewBox={`0 0 256 256`}
                            />
                        </div>
                    </div>
                </div>
                <div className='content'>{children}</div>
            </div>
            {!imageId && userCreatedReferral && (
                <div className='user-ranking-overlay'>
                    <div className='loading-container'>
                        <Spinner />
                        <span>Uploading Image...</span>
                    </div>
                </div>
            )}
        </div>
    );
};
export default ShareCardBase;
